import React, { useState, useRef, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreateCustomer, useUpdateCustomer } from '../../mutations/customerMutations';

// ─── Constants ────────────────────────────────────────────────────────────────
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PHONE_REGEX = /^\+?[0-9\s\-()]{7,20}$/;

const SALES_MODE_OPTIONS = [
  { value: '', label: 'Select Mode' },
  { value: 'cash', label: 'CASH' },
  { value: 'credit', label: 'CREDIT' },
];

const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

// ─── Month Picker ────────────────────────────────────────────────────────────
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MonthPicker = ({ id, value, onChange, placeholder = 'Jan', errors }) => {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(() => {
    if (value && value.includes('-')) return parseInt(value.split('-')[0], 10);
    return new Date().getFullYear();
  });
  const containerRef = useRef(null);

  // Derive display text & selected index from stored value ("YYYY-MM")
  const selectedIdx = value && value.includes('-') ? parseInt(value.split('-')[1], 10) - 1 : -1;
  const displayValue = selectedIdx >= 0 ? MONTHS[selectedIdx] : '';

  useEffect(() => {
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleSelect = (idx) => {
    onChange(`${year}-${String(idx + 1).padStart(2, '0')}`);
    setOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        id={id}
        type="text"
        readOnly
        value={displayValue}
        placeholder={placeholder}
        className={`form-control${errors ? ' is-invalid' : ''}`}
        onClick={() => setOpen((o) => !o)}
        style={{ cursor: 'pointer' }}
        aria-label="Select month"
      />

      {open && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 1050,
            background: '#fff', border: '1px solid #dee2e6',
            borderRadius: '0.375rem', boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
            padding: '0.75rem', minWidth: '210px',
          }}
        >
          {/* Year row */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <button type="button" className="btn btn-sm btn-link text-dark p-0 px-2"
              onClick={() => setYear((y) => y - 1)}>&#8249;</button>
            <span className="fw-semibold">{year}</span>
            <button type="button" className="btn btn-sm btn-link text-dark p-0 px-2"
              onClick={() => setYear((y) => y + 1)}>&#8250;</button>
          </div>
          {/* Month grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' }}>
            {MONTHS.map((m, i) => (
              <button
                key={m}
                type="button"
                onClick={() => handleSelect(i)}
                style={{
                  border: 'none', borderRadius: '0.25rem', padding: '0.3rem 0',
                  cursor: 'pointer', fontSize: '0.85rem',
                  background: selectedIdx === i ? '#0d6efd' : 'transparent',
                  color: selectedIdx === i ? '#fff' : '#212529',
                  fontWeight: selectedIdx === i ? 600 : 400,
                }}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const EMPTY_BANK = {
  id: 0,
  bankName: '',
  accountHolderName: '',
  accountNumber: '',
  ifscCode: '',
  branchName: '',
  swiftCode: '',
  isPrimary: false,
};

const EMPTY_SHIPPING = {
  id: 0,
  shippingMobileNumber: '',
  shippingDoorNo: '',
  shippingStreet: '',
  shippingLandmark: '',
  shippingCity: '',
  shippingDistrict: '',
  shippingState: '',
  shippingPinCode: '',
  kiloMeter: 0,
};

// ─── Default Values ───────────────────────────────────────────────────────────
const defaultValues = {
  // Identity
  customerId: '',
  customerName: '',
  ownerName: '',
  mobileNumber: '',
  emailId: '',
  msmeNumber: '',
  aadhaarNumber: '',
  gstNumber: '',
  salesMode: '',
  status: 'Active',
  entityStatus: 'Active',
  password: '',
  pandlPassword: '',
  reminderMonth: '',
  items: '',
  panNumber: '',
  iecCode: '',
  website: '',
  creditLimit: 0,
  paymentTerms: '',
  defaultCurrency: '',
  isActive: true,
  // Billing Address
  billingDoorNo: '',
  billingStreet: '',
  billingLandmark: '',
  billingCity: '',
  billingDistrict: '',
  billingState: '',
  billingCountry: '',
  billingPinCode: '',
  // Sub-collections
  bankDetails: [{ ...EMPTY_BANK }],
  shippingAddresses: [{ ...EMPTY_SHIPPING }],
};

// ─── Reusable: Controlled Input / Textarea ────────────────────────────────────
const Field = ({ id, label, name, control, rules = {}, type = 'text', placeholder = '', as: Tag = 'input', errors }) => (
  <div className="mb-2">
    <label htmlFor={id} className="form-label fw-semibold small text-uppercase tw-text-[#1F2937] mb-1">
      {label}
      {rules.required && <span className="text-danger ms-1" aria-hidden="true">*</span>}
    </label>
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) =>
        Tag === 'textarea' ? (
          <textarea
            {...field}
            id={id}
            rows={4}
            className={`form-control${errors ? ' is-invalid' : ''}`}
            placeholder={placeholder}
            aria-label={label}
            aria-invalid={!!errors}
            aria-describedby={errors ? `${id}-err` : undefined}
          />
        ) : (
          <input
            {...field}
            id={id}
            type={type}
            className={`form-control${errors ? ' is-invalid' : ''}`}
            placeholder={placeholder}
            aria-label={label}
            aria-invalid={!!errors}
            aria-describedby={errors ? `${id}-err` : undefined}
          />
        )
      }
    />
    {errors && (
      <div id={`${id}-err`} className="invalid-feedback d-block" role="alert">
        {errors.message}
      </div>
    )}
  </div>
);

// ─── Reusable: Controlled Select ──────────────────────────────────────────────
const SelectField = ({ id, label, name, control, rules = {}, options, errors }) => (
  <div className="mb-2">
    <label htmlFor={id} className="form-label fw-semibold small text-uppercase tw-text-[#1F2937] mb-1">
      {label}
      {rules.required && <span className="text-danger ms-1" aria-hidden="true">*</span>}
    </label>
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <select
          {...field}
          id={id}
          className={`form-select${errors ? ' is-invalid' : ''}`}
          aria-label={label}
          aria-invalid={!!errors}
          aria-describedby={errors ? `${id}-err` : undefined}
        >
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
    />
    {errors && (
      <div id={`${id}-err`} className="invalid-feedback d-block" role="alert">
        {errors.message}
      </div>
    )}
  </div>
);

// ─── Reusable: Section Divider ──────────────────────────────────────────────
const Section = ({ title, children }) => (
  <div className="mb-3">
    <h2 className="text-center fw-bold text-uppercase fs-6 py-2 mb-2 border-bottom border-top gtss-text-primary">
      {title}
    </h2>
    {children}
  </div>
);


// ─── Main Component ───────────────────────────────────────────────────────────
/**
 * AddCustomer — Complete customer form
 * Bootstrap 5 grid + Bootstrap form classes.
 * GTSS colors via tw- Tailwind prefix only.
 * React Hook Form: Controller + useFieldArray. No register().
 */
const AddCustomer = () => {
  // ─── Detect Edit vs Create mode ───────────────────────────────────────────
  // When navigating from ViewCustomer with { state: customerData }, we are in
  // edit mode. If no state is present, we are creating a new customer.
  const { state: routeState } = useLocation();
  const navigate = useNavigate();
  console.log('state', routeState); // ← inspect incoming row data

  // Prefer MongoDB _id; fall back to customerId, then plain id (sample data)
  // Use the database-generated integer id for PUT/DELETE
  const editId = routeState?.id ?? null;
  const isEditMode = !!editId;

  // ─── React Query Mutations ────────────────────────────────────────────────
  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues });

  // Bank Details array
  const {
    fields: bankFields,
    append: appendBank,
    remove: removeBank,
  } = useFieldArray({ control, name: 'bankDetails' });

  // Shipping Addresses array
  const { fields, append, remove } = useFieldArray({ control, name: 'shippingAddresses' });

  // P&L Password show/hide toggle
  const [showPandlPassword, setShowPandlPassword] = useState(false);

  // ─── Pre-fill form when editing ───────────────────────────────────────────
  // Uses reset() — one clean call instead of many setValue() calls.
  // Only runs when routeState changes (i.e., when navigating to edit).
  useEffect(() => {
    if (isEditMode && routeState) {
      reset({
        // Identity
        customerId:       routeState.customerId       ?? defaultValues.customerId,
        customerName:     routeState.customerName     ?? defaultValues.customerName,
        ownerName:        routeState.ownerName        ?? routeState.owner ?? defaultValues.ownerName,
        mobileNumber:     routeState.mobileNumber     ?? routeState.mobileNo ?? defaultValues.mobileNumber,
        emailId:          routeState.emailId          ?? defaultValues.emailId,
        msmeNumber:       routeState.msmeNumber       ?? defaultValues.msmeNumber,
        aadhaarNumber:    routeState.aadhaarNumber    ?? defaultValues.aadhaarNumber,
        gstNumber:        routeState.gstNumber        ?? defaultValues.gstNumber,
        salesMode:        routeState.salesMode        ?? routeState.sales ?? defaultValues.salesMode,
        status:           routeState.status           ?? routeState.entityStatus ?? (routeState.isActive === false ? 'Inactive' : defaultValues.status),
        entityStatus:     routeState.entityStatus     ?? routeState.status ?? (routeState.isActive === false ? 'Inactive' : defaultValues.entityStatus),
        password:         routeState.password         ?? defaultValues.password,
        pandlPassword:    routeState.pandlPassword    ?? defaultValues.pandlPassword,
        reminderMonth:    routeState.reminderMonth    ?? routeState.month ?? defaultValues.reminderMonth,
        items:            routeState.items            ?? routeState.reminderItems ?? defaultValues.items,
        panNumber:        routeState.panNumber        ?? defaultValues.panNumber,
        iecCode:          routeState.iecCode          ?? defaultValues.iecCode,
        website:          routeState.website          ?? defaultValues.website,
        creditLimit:      routeState.creditLimit      ?? defaultValues.creditLimit,
        paymentTerms:     routeState.paymentTerms     ?? defaultValues.paymentTerms,
        defaultCurrency:  routeState.defaultCurrency  ?? defaultValues.defaultCurrency,
        isActive:         routeState.isActive         ?? defaultValues.isActive,
        // Billing Address (Flat fields in API, handles legacy nested fallback)
        billingDoorNo:    routeState.billingDoorNo    ?? routeState.billingAddress?.doorNo   ?? defaultValues.billingDoorNo,
        billingStreet:    routeState.billingStreet    ?? routeState.billingAddress?.street   ?? defaultValues.billingStreet,
        billingLandmark:  routeState.billingLandmark  ?? routeState.billingAddress?.landmark ?? defaultValues.billingLandmark,
        billingCity:      routeState.billingCity      ?? routeState.billingAddress?.city     ?? defaultValues.billingCity,
        billingDistrict:  routeState.billingDistrict  ?? routeState.billingAddress?.district ?? defaultValues.billingDistrict,
        billingState:     routeState.billingState     ?? routeState.billingAddress?.state    ?? defaultValues.billingState,
        billingCountry:   routeState.billingCountry   ?? routeState.billingAddress?.country  ?? defaultValues.billingCountry,
        billingPinCode:   routeState.billingPinCode   ?? routeState.billingAddress?.pincode  ?? defaultValues.billingPinCode,
        // Sub-collections
        bankDetails:      routeState.bankDetails?.length > 0
          ? routeState.bankDetails.map(b => ({
              id:                 b.id                 ?? 0,
              bankName:           b.bankName           ?? '',
              accountHolderName:  b.accountHolderName  ?? '',
              accountNumber:      b.accountNumber      ?? '',
              ifscCode:           b.ifscCode           ?? '',
              branchName:         b.branchName         ?? '',
              swiftCode:          b.swiftCode          ?? '',
              isPrimary:          b.isPrimary          ?? false,
            }))
          : defaultValues.bankDetails,
        shippingAddresses: routeState.shippingAddresses?.map(s => ({
          id:                   s.id ?? 0,
          shippingMobileNumber: s.shippingMobileNumber ?? s.mobileNumber ?? '',
          shippingDoorNo:       s.shippingDoorNo       ?? s.doorNo       ?? '',
          shippingStreet:       s.shippingStreet       ?? s.street       ?? '',
          shippingLandmark:     s.shippingLandmark     ?? s.landmark     ?? '',
          shippingCity:         s.shippingCity         ?? s.city         ?? '',
          shippingDistrict:     s.shippingDistrict     ?? s.district     ?? '',
          shippingState:        s.shippingState        ?? s.state        ?? '',
          shippingPinCode:      s.shippingPinCode      ?? s.pincode      ?? '',
          kiloMeter:            s.kiloMeter            ?? s.distanceInKm ?? 0,
        })) || defaultValues.shippingAddresses,
      });
    }
  }, [isEditMode, routeState, reset]);

  const onSubmit = (formData) => {
    const data = {
      ...formData,
      password: formData.password || '',
      status: formData.status || formData.entityStatus || 'Active',
      entityStatus: formData.status || formData.entityStatus || 'Active',
      isActive: (formData.status || formData.entityStatus) === 'Active',
    };
    console.log('onSubmit data', data); // ← inspect full form payload
    if (isEditMode) {
      // UPDATE — PUT /customers/:id
      updateMutation.mutate({ id: editId, data }, {
        onSuccess: () => {
          navigate('/customer/view');
        },
      });
    } else {
      // CREATE — call POST /customers
      createMutation.mutate(data, {
        onSuccess: () => {
          navigate('/customer/view');
        },
      });
    }
  };

  // Combined loading state for the submit button
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="h-100 d-flex flex-column" noValidate>
      <div className="card shadow-sm border rounded-top-0 rounded-bottom-3 p-3 p-md-4 d-flex flex-column flex-grow-1">

        <div className="flex-grow-1">

          {/* ── 1. Customer Details ─────────────────────────────────── */}
          <Section title="Customer Details">
            <div className="row gx-3 gy-2">

              <div className="col-12 col-md-6 col-lg-4">
                <Field id="customerName" label="Customer Name"
                  name="customerName" control={control}
                  placeholder="Enter customer name"
                  rules={{ required: 'Customer name is required.' }}
                  errors={errors.customerName} />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <Field id="ownerName" label="Owner Name"
                  name="ownerName" control={control}
                  placeholder="Enter owner name" errors={errors.ownerName} />
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Field id="mobileNumber" label="Mobile Number"
                  name="mobileNumber" control={control} type="tel"
                  placeholder="e.g. +91 9876543210"
                  rules={{
                    required: 'Mobile number is required.',
                    pattern: {
                      value: PHONE_REGEX,
                      message: 'Please enter a valid mobile number with country code (e.g. +91 9876543210).',
                    },
                  }}
                  errors={errors.mobileNumber} />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <Field id="emailId" label="Email ID"
                  name="emailId" control={control} type="email"
                  placeholder="example@email.com"
                  rules={{ pattern: { value: EMAIL_REGEX, message: 'Enter a valid email.' } }}
                  errors={errors.emailId} />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <Field id="msmeNumber" label="MSME Number"
                  name="msmeNumber" control={control}
                  placeholder="MSME registration no." errors={errors.msmeNumber} />
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Field id="aadhaarNumber" label="Aadhaar Number"
                  name="aadhaarNumber" control={control}
                  placeholder="12-digit Aadhaar" errors={errors.aadhaarNumber} />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <Field id="gstNumber" label="GST Number"
                  name="gstNumber" control={control}
                  placeholder="e.g. 33GRDPS9509A1Z5"
                  rules={{ pattern: { value: GST_REGEX, message: 'Enter a valid GST number.' } }}
                  errors={errors.gstNumber} />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <SelectField id="salesMode" label="Sales Mode"
                  name="salesMode" control={control}
                  options={SALES_MODE_OPTIONS} errors={errors.salesMode} />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <SelectField id="status" label="Status"
                  name="status" control={control}
                  options={STATUS_OPTIONS} errors={errors.status} />
              </div>

              {/* P&L Password */}
              <div className="col-12 col-md-6 col-lg-4">
                <div className="mb-2">
                  <label htmlFor="pandlPassword" className="form-label fw-semibold small text-uppercase tw-text-[#1F2937] mb-1">
                    P&amp;L Password
                  </label>
                  <div className="input-group">
                    <Controller
                      name="pandlPassword"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          id="pandlPassword"
                          type={showPandlPassword ? 'text' : 'password'}
                          className="form-control"
                          placeholder="Enter P&L password"
                          aria-label="P&L Password"
                        />
                      )}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary d-flex align-items-center"
                      onClick={() => setShowPandlPassword(!showPandlPassword)}
                      aria-label={showPandlPassword ? 'Hide P&L password' : 'Show P&L password'}
                    >
                      {showPandlPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a18.883 18.883 0 0 0-2.79.208l-.77-.77a18.847 18.847 0 0 1 3.56-.277c2.02 0 3.82.668 5.168 1.957A13.133 13.133 0 0 1 14.828 8a13.125 13.125 0 0 1-3.078 3.038l-.77-.77a11.11 11.11 0 0 0 2.38-2.268zM8 5.5a2.5 2.5 0 1 0 2.242 3.738l-2.242-2.242A2.5 2.5 0 0 0 8 5.5z"/>
                          <path d="M10.97 10.97a7.5 7.5 0 0 0 1.073-.707l.86.86a8.966 8.966 0 0 1-1.933 1.073l-.707-1.073zm-2.062 1.343A8.96 8.96 0 0 1 8 12.5c-2.02 0-3.82-.668-5.168-1.957A13.134 13.134 0 0 1 1.172 8c.058-.088.122-.183.195-.288.335-.48.83-1.12 1.465-1.755a8.966 8.966 0 0 1 1.637-1.294l.707 1.073A7.488 7.488 0 0 0 4.108 8a11.11 11.11 0 0 0 2.579 2.457l.77.77zm-.707-3.693l-.707-.707a2.5 2.5 1 0 0 3.536 3.536l-.707-.707a1.5 1.5 0 0 1-2.122-2.122z"/>
                          <path d="M12.438 10.468L1.47 1.53l.73-.73 10.968 8.937-.73.73z"/>
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 4 8 4c2.02 0 3.82.668 5.167 1.957A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12 8 12c-2.02 0-3.82-.668-5.167-1.957A13.133 13.133 0 0 1 1.172 8z"/>
                          <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* ── 2. Reminder ─────────────────────────────────────────── */}
          <Section title="Reminder">
            <div className="row gx-3 gy-2">
              <div className="col-12 col-md-6 col-lg-4">
                <div className="mb-2">
                  <label htmlFor="reminderMonth" className="form-label fw-semibold small text-uppercase tw-text-[#1F2937] mb-1">
                    Month
                  </label>
                  <Controller
                    name="reminderMonth"
                    control={control}
                    render={({ field }) => (
                      <MonthPicker
                        id="reminderMonth"
                        value={field.value}
                        onChange={field.onChange}
                        errors={errors.reminderMonth}
                      />
                    )}
                  />
                  {errors.reminderMonth && (
                    <div className="invalid-feedback d-block" role="alert">
                      {errors.reminderMonth.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12">
                <Field id="items" label="Items"
                  name="items" control={control}
                  as="textarea" placeholder="e.g. Logs, Timber..."
                  errors={errors.items} />
              </div>
            </div>
          </Section>

          {/* ── 3. Billing Address ──────────────────────────────────── */}
          <Section title="Billing Address">
            <div className="row gx-3 gy-2">
              {[
                { id: 'b-doorNo', label: 'Door No', name: 'billingDoorNo', ph: 'Door number' },
                { id: 'b-street', label: 'Street', name: 'billingStreet', ph: 'Street name' },
                { id: 'b-landmark', label: 'Landmark', name: 'billingLandmark', ph: 'Nearby landmark' },
                { id: 'b-city', label: 'City', name: 'billingCity', ph: 'City' },
                { id: 'b-district', label: 'District', name: 'billingDistrict', ph: 'District' },
                { id: 'b-state', label: 'State', name: 'billingState', ph: 'State' },
              ].map(({ id, label, name, ph }) => (
                <div key={id} className="col-12 col-md-6 col-lg-4">
                  <Field id={id} label={label} name={name} control={control}
                    placeholder={ph} errors={errors[name]} />
                </div>
              ))}
              <div className="col-12 col-md-6 col-lg-4">
                <Field id="b-pincode" label="Pincode"
                  name="billingPinCode" control={control}
                  placeholder="6-digit pincode"
                  rules={{ pattern: { value: /^[0-9]{6}$/, message: 'Enter a valid 6-digit pincode.' } }}
                  errors={errors.billingPinCode} />
              </div>
            </div>
          </Section>

          {/* ── 4. Shipping Address (dynamic) ───────────────────────── */}
          <Section title="Shipping Address">
            {fields.map((field, idx) => (
              <div key={field.id} className="border rounded-3 p-3 mb-2 gtss-bg-light">

                {/* Sub-header */}
                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                  <h3 className="mb-0 fs-6 fw-bold text-uppercase gtss-text-primary">
                    Address {idx + 1}
                  </h3>
                  {idx > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      aria-label={`Remove address ${idx + 1}`}
                      onClick={() => remove(idx)}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="row gx-3 gy-2">
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field id={`s${idx}-mobile`} label="Mobile Number"
                      name={`shippingAddresses.${idx}.shippingMobileNumber`} control={control}
                      type="tel" placeholder="e.g. +91 9876543210"
                      rules={{
                        pattern: {
                          value: PHONE_REGEX,
                          message: 'Please enter a valid mobile number with country code.',
                        },
                      }}
                      errors={errors.shippingAddresses?.[idx]?.shippingMobileNumber} />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field id={`s${idx}-doorNo`} label="Door No"
                      name={`shippingAddresses.${idx}.shippingDoorNo`} control={control}
                      placeholder="Door number"
                      errors={errors.shippingAddresses?.[idx]?.shippingDoorNo} />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field id={`s${idx}-street`} label="Street"
                      name={`shippingAddresses.${idx}.shippingStreet`} control={control}
                      placeholder="Street name"
                      errors={errors.shippingAddresses?.[idx]?.shippingStreet} />
                  </div>

                  <div className="col-12 col-md-6 col-lg-4">
                    <Field id={`s${idx}-landmark`} label="Landmark"
                      name={`shippingAddresses.${idx}.shippingLandmark`} control={control}
                      placeholder="Nearby landmark"
                      errors={errors.shippingAddresses?.[idx]?.shippingLandmark} />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field id={`s${idx}-city`} label="City"
                      name={`shippingAddresses.${idx}.shippingCity`} control={control}
                      placeholder="City"
                      errors={errors.shippingAddresses?.[idx]?.shippingCity} />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field id={`s${idx}-district`} label="District"
                      name={`shippingAddresses.${idx}.shippingDistrict`} control={control}
                      placeholder="District"
                      errors={errors.shippingAddresses?.[idx]?.shippingDistrict} />
                  </div>

                  <div className="col-12 col-md-6 col-lg-4">
                    <Field id={`s${idx}-state`} label="State"
                      name={`shippingAddresses.${idx}.shippingState`} control={control}
                      placeholder="State"
                      errors={errors.shippingAddresses?.[idx]?.shippingState} />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field id={`s${idx}-pincode`} label="Pincode"
                      name={`shippingAddresses.${idx}.shippingPinCode`} control={control}
                      placeholder="6-digit pincode"
                      rules={{ pattern: { value: /^[0-9]{6}$/, message: 'Enter a valid 6-digit pincode.' } }}
                      errors={errors.shippingAddresses?.[idx]?.shippingPinCode} />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field id={`s${idx}-km`} label="Kilo-Meter (KM)"
                      name={`shippingAddresses.${idx}.kiloMeter`} control={control}
                      type="number" placeholder="Distance in KM"
                      rules={{ min: { value: 0, message: 'KM must be a positive number.' } }}
                      errors={errors.shippingAddresses?.[idx]?.kiloMeter} />
                  </div>
                </div>
              </div>
            ))}

            {/* Add Address */}
            <button
              type="button"
              id="add-address-btn"
              className="btn w-100 fw-semibold text-white gtss-bg-secondary border-0"
              aria-label="Add another shipping address"
              onClick={() => append({ ...EMPTY_SHIPPING })}
            >
              + Add Address
            </button>
          </Section>

          {/* ── 5. Bank Details (Dynamic Array) ───────────────────────── */}
          <Section title="Bank Details">
            {bankFields.map((field, idx) => (
              <div key={field.id} className="border rounded-3 p-3 mb-2 gtss-bg-light">
                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                  <h3 className="mb-0 fs-6 fw-bold text-uppercase gtss-text-primary">
                    Bank Account {idx + 1}
                  </h3>
                  {idx > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      aria-label={`Remove bank account ${idx + 1}`}
                      onClick={() => removeBank(idx)}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="row gx-3 gy-2">
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field
                      id={`bankName-${idx}`}
                      label="Bank Name"
                      name={`bankDetails.${idx}.bankName`}
                      control={control}
                      placeholder="Enter bank name"
                      errors={errors.bankDetails?.[idx]?.bankName}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field
                      id={`accountHolderName-${idx}`}
                      label="Account Holder Name"
                      name={`bankDetails.${idx}.accountHolderName`}
                      control={control}
                      placeholder="Enter account holder name"
                      errors={errors.bankDetails?.[idx]?.accountHolderName}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field
                      id={`accountNumber-${idx}`}
                      label="Account Number"
                      name={`bankDetails.${idx}.accountNumber`}
                      control={control}
                      placeholder="Enter account number"
                      errors={errors.bankDetails?.[idx]?.accountNumber}
                    />
                  </div>

                  <div className="col-12 col-md-6 col-lg-4">
                    <Field
                      id={`ifscCode-${idx}`}
                      label="IFSC Code"
                      name={`bankDetails.${idx}.ifscCode`}
                      control={control}
                      placeholder="Enter IFSC code"
                      errors={errors.bankDetails?.[idx]?.ifscCode}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field
                      id={`branchName-${idx}`}
                      label="Branch Name"
                      name={`bankDetails.${idx}.branchName`}
                      control={control}
                      placeholder="Enter branch name"
                      errors={errors.bankDetails?.[idx]?.branchName}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field
                      id={`swiftCode-${idx}`}
                      label="Swift Code"
                      name={`bankDetails.${idx}.swiftCode`}
                      control={control}
                      placeholder="Enter SWIFT code"
                      errors={errors.bankDetails?.[idx]?.swiftCode}
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              id="add-bank-btn"
              className="btn w-100 fw-semibold text-white gtss-bg-secondary border-0 mb-2"
              aria-label="Add another bank account"
              onClick={() => appendBank({ ...EMPTY_BANK })}
            >
              + Add Bank Account
            </button>
          </Section>
        </div>

        {/* Submit */}
        <div className="text-center pt-2 pb-1 mt-auto">
          <button
            type="submit"
            id="submit-customer-btn"
            className="btn px-5 py-2 fw-semibold text-white gtss-bg-primary border-0"
            disabled={isPending}
            aria-label={isEditMode ? 'Update customer' : 'Submit customer form'}
          >
            {isPending
              ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Saving...</>
              : isEditMode ? 'Update' : 'Submit'
            }
          </button>
        </div>

      </div>
    </form>
  );
};

export default AddCustomer;